import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "../ui/button";
import MagneticButton from "../ui/MagneticButton";

const LOGO = "/logo-kb-icon.png";

// type: "scroll" = smooth-scroll to anchor (on homepage) or navigate to /#anchor (on subpages)
// type: "link"   = React Router page navigation
const NAV_ITEMS = [
    { label: "Domov",      type: "link",   href: "/"          },
    { label: "Služby",     type: "scroll", href: "#services"  },
    { label: "O nás",      type: "link",   href: "/o-nas"     },
    { label: "Referencie", type: "link",   href: "/referencie" },
    { label: "Technika",   type: "link",   href: "/technika"  },
    { label: "Prenájom",   type: "link",   href: "/prenajom", accent: true },
    { label: "Predaj",     type: "link",   href: "/predaj"   },
    { label: "3D Vizualizácie", type: "link", href: "/vizualizacie" },
    { label: "Kontakt",    type: "link",   href: "/kontakt"   },
];

export default function Navigation() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const location = useLocation();

    const scrollTo = (href) => {
        setOpen(false);
        if (location.pathname === "/") {
            const el = document.querySelector(href);
            if (el) el.scrollIntoView({ behavior: "smooth" });
        } else {
            window.location.href = `/${href}`;
        }
    };

    return (
        <motion.nav
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-nova-black/80 backdrop-blur-xl border-b border-white/5"
                    : "bg-transparent"
            }`}
            data-testid="main-navigation"
        >
            <div className="max-w-7xl mx-auto px-6 sm:px-12 h-20 flex items-center justify-between">
                <Link
                    to="/"
                    className="flex items-center gap-3 group"
                    data-testid="nav-logo"
                >
                    <div className="relative">
                        <div className="absolute inset-0 bg-nova-cyan blur-xl opacity-20 group-hover:opacity-40 transition-opacity rounded-full" />
                        <img
                            src={LOGO}
                            alt="KB MUSIC"
                            className="relative w-11 h-11 rounded-full object-contain ring-1 ring-nova-cyan/30 group-hover:ring-nova-cyan/70 transition-all"
                        />
                    </div>
                    <span className="font-heading font-black text-xl tracking-tight hidden sm:inline">
                        KB<span className="text-nova-cyan"> </span>MUSIC
                    </span>
                </Link>

                <div className="hidden lg:flex items-center gap-1">
                    {NAV_ITEMS.map((item) => {
                        const cls = `px-4 py-2 text-sm font-medium transition-colors relative group ${
                            item.accent ? "text-nova-cyan/80 hover:text-nova-cyan" : "text-zinc-400 hover:text-white"
                        }`;
                        const underline = <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-px bg-nova-cyan group-hover:w-6 transition-all duration-300" />;
                        return item.type === "link" ? (
                            <Link key={item.href} to={item.href} className={cls}>
                                {item.label}{underline}
                            </Link>
                        ) : (
                            <button key={item.href} onClick={() => scrollTo(item.href)} className={cls}>
                                {item.label}{underline}
                            </button>
                        );
                    })}
                </div>

                <div className="hidden lg:block">
                    <MagneticButton strength={0.25}>
                        <Button
                            onClick={() => scrollTo("#contact")}
                            className="bg-nova-cyan text-black hover:bg-nova-cyan hover:saturate-125 font-semibold rounded-md px-6 h-10 text-sm transition-all hover:shadow-[0_0_20px_rgba(0,153,255,0.35)]"
                            data-testid="nav-cta-button"
                        >
                            Nezáväzná ponuka
                        </Button>
                    </MagneticButton>
                </div>

                <button
                    className="lg:hidden text-white p-2"
                    onClick={() => setOpen((v) => !v)}
                    data-testid="nav-mobile-toggle"
                    aria-label={open ? "Zatvoriť menu" : "Otvoriť menu"}
                    aria-expanded={open}
                >
                    {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="lg:hidden bg-nova-black/95 backdrop-blur-xl border-t border-white/5 overflow-hidden"
                        data-testid="nav-mobile-menu"
                    >
                        <div className="px-6 py-6 flex flex-col gap-1">
                            {NAV_ITEMS.map((item) =>
                                item.type === "link" ? (
                                    <Link
                                        key={item.href}
                                        to={item.href}
                                        onClick={() => setOpen(false)}
                                        className={`py-3 font-medium transition-colors ${item.accent ? "text-nova-cyan" : "text-zinc-300 hover:text-nova-cyan"}`}
                                    >
                                        {item.label}
                                    </Link>
                                ) : (
                                    <button
                                        key={item.href}
                                        onClick={() => scrollTo(item.href)}
                                        className="text-left py-3 text-zinc-300 hover:text-nova-cyan transition-colors font-medium"
                                    >
                                        {item.label}
                                    </button>
                                )
                            )}
                            <Link
                                to="/kontakt"
                                onClick={() => setOpen(false)}
                                className="mt-3 flex items-center justify-center h-10 rounded-md bg-nova-cyan text-black font-semibold text-sm"
                                data-testid="nav-mobile-cta"
                            >
                                Nezáväzná ponuka
                            </Link>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.nav>
    );
}
